import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserDoctor, faPaperPlane, faCommentDots } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../config/api.config';
import {
  CLINIC_PHONE,
  CLINIC_PHONE_TEL,
  CLINIC_PHONE_WHATSAPP,
  DEFAULT_DOCTOR_DETAILS,
} from '../config/clinic.config';
import BackButton from './BackButton';
import './DoctorConsultation.css';

const toTelHref = (phone) => {
  const digits = String(phone || '').replace(/[^\d+]/g, '');
  return digits || CLINIC_PHONE_TEL;
};

const toWhatsAppNumber = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return CLINIC_PHONE_WHATSAPP;
  if (digits.startsWith('92')) return digits;
  if (digits.startsWith('0')) return `92${digits.slice(1)}`;
  return digits;
};

const detailLines = (details) =>
  String(details || DEFAULT_DOCTOR_DETAILS)
    .trim()
    .split(/\r?\n/)
    .filter(Boolean);

const DoctorConsultation = ({ onBackClick, user, onLoginClick }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [activeDoctorId, setActiveDoctorId] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState('');

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}?action=doctors`);
      const data = await response.json();
      if (data.status === 'ok') {
        setDoctors(data.doctors || []);
      }
    } catch (err) {
      console.error('Error fetching doctors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_BASE_URL}?action=messages&userId=${user.id}`);
      const data = await response.json();
      if (data.status === 'ok') {
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [user]);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleSend = async (doctorId) => {
    if (!user) {
      if (onLoginClick) onLoginClick();
      return;
    }
    if (!draft.trim()) return;
    setSending(true);
    setFeedback('');
    try {
      const response = await fetch(`${API_BASE_URL}?action=send_message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          doctorId,
          senderRole: 'user',
          body: draft.trim(),
        }),
      });
      const data = await response.json();
      if (data.status === 'ok') {
        setDraft('');
        setFeedback('Message sent! The doctor will respond here shortly.');
        fetchMessages();
      } else {
        setFeedback(data.message || 'Failed to send message.');
      }
    } catch (err) {
      console.error('Send message error:', err);
      setFeedback('Network error while sending message.');
    } finally {
      setSending(false);
    }
  };

  const messagesForDoctor = (doctorId) =>
    messages.filter((m) => Number(m.doctor_id) === Number(doctorId));

  return (
    <div className="doctor-consultation">
      {onBackClick && <BackButton onClick={onBackClick} />}
      <h2>Consult a Doctor</h2>
      <p>Get professional medical advice from our healthcare professionals.</p>

      {loading ? (
        <div className="doctor-loading">Loading doctors...</div>
      ) : doctors.length === 0 ? (
        <div className="doctor-empty">No doctor is available at the moment. Please check back later.</div>
      ) : (
        <div className="doctors-grid">
          {doctors.map((doctor) => {
            const convo = messagesForDoctor(doctor.id);
            const isActive = activeDoctorId === doctor.id;
            const phone = doctor.phone || CLINIC_PHONE;
            const lines = detailLines(doctor.details);
            return (
              <div key={doctor.id} className="doctor-card">
                <div className="doctor-avatar">
                  <FontAwesomeIcon icon={faUserDoctor} />
                </div>
                <h3>{doctor.name}</h3>
                {lines.map((line, index) => (
                  <p key={`${doctor.id}-${index}`} className={index === 0 ? 'specialty' : 'experience'}>
                    {line}
                  </p>
                ))}
                <p className="experience">Phone: {phone}</p>

                <div className="doctor-actions">
                  <a
                    href={`https://wa.me/${toWhatsAppNumber(phone)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-action whatsapp"
                  >
                    📱 WhatsApp
                  </a>
                  <a href={`tel:${toTelHref(phone)}`} className="btn-action call">
                    📞 Call {phone}
                  </a>
                  <button
                    className="btn-action message"
                    onClick={() => {
                      setActiveDoctorId(isActive ? null : doctor.id);
                      setFeedback('');
                    }}
                  >
                    <FontAwesomeIcon icon={faCommentDots} /> {isActive ? 'Close chat' : 'Message'}
                  </button>
                </div>

                {isActive && (
                  <div className="doctor-chat">
                    {!user ? (
                      <p className="chat-login-hint">
                        Please{' '}
                        <button type="button" className="chat-login-link" onClick={onLoginClick}>
                          login
                        </button>{' '}
                        to message this doctor.
                      </p>
                    ) : (
                      <>
                        <div className="chat-thread">
                          {convo.length === 0 ? (
                            <p className="chat-empty">No messages yet. Start the conversation below.</p>
                          ) : (
                            convo.map((m) => (
                              <div
                                key={m.id}
                                className={`chat-bubble ${m.sender_role === 'doctor' ? 'from-doctor' : 'from-user'}`}
                              >
                                <span className="chat-sender">
                                  {m.sender_role === 'doctor' ? doctor.name : 'You'}
                                </span>
                                <span className="chat-body">{m.body}</span>
                                <span className="chat-time">
                                  {new Date(m.created_at).toLocaleString()}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="chat-composer">
                          <textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder="Type your message to the doctor..."
                            rows="2"
                          />
                          <button
                            className="chat-send"
                            onClick={() => handleSend(doctor.id)}
                            disabled={sending || !draft.trim()}
                          >
                            <FontAwesomeIcon icon={faPaperPlane} /> {sending ? 'Sending...' : 'Send'}
                          </button>
                        </div>
                        {feedback && <p className="chat-feedback">{feedback}</p>}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DoctorConsultation;
